// Validate both streamed answers and persisted/refresh contracts at the boundary.
import type { Answer, Chart } from '../types'

const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)
const finite = (value: unknown) => typeof value === 'number' && Number.isFinite(value)

export function isChart(value: unknown): value is Chart {
  if (!object(value) || !['barH', 'barV', 'line', 'groupedH', 'donut'].includes(String(value.type))) return false
  if (typeof value.unit !== 'string' || (value.ref != null && !finite(value.ref))) return false
  if (value.series != null && (!Array.isArray(value.series) || !value.series.every((s) => typeof s === 'string'))) return false
  if (!Array.isArray(value.data) || value.data.length === 0) return false
  if (value.series != null && !['line', 'groupedH'].includes(String(value.type))) return false
  const keys = value.series ?? (value.type === 'groupedH' ? null : ['value'])
  if (!Array.isArray(keys) || keys.length === 0 || new Set(keys).size !== keys.length
    || keys.some((key) => !key.trim() || ['name', 'period', 'partial', 'period_from', 'period_to', 'period_kind', 'coverage', 'missing_days'].includes(key))) return false
  if (new Set(value.data.map((row) => object(row) ? row.name : undefined)).size !== value.data.length) return false
  if (!value.data.every((row) => object(row) && typeof row.name === 'string'
    && keys.every((key) => key in row && (row[key] === null || finite(row[key]))))) return false
  if (value.projection != null && (!object(value.projection) || !finite(value.projection.low) || !finite(value.projection.high))) return false
  return true
}

export function isAnswer(value: unknown): value is Answer {
  if (!object(value) || typeof value.answer !== 'string' || !value.answer.trim()) return false
  if (value.caveat != null && typeof value.caveat !== 'string') return false
  if (value.caveat_info != null && typeof value.caveat_info !== 'string') return false
  if (value.spec != null && !object(value.spec)) return false
  if (['preliminary', 'pinnable', 'stale'].some((key) => value[key] != null && typeof value[key] !== 'boolean')) return false
  return value.chart == null || isChart(value.chart)
}
